import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { getBusinessContract, getContractTemplates } from '@/services/business-opportunities';
import { BusinessContract, ContractStatus, SignatoryRole, ContractSignatory } from '@/types/business-opportunities';
import { formatCurrency } from '@/lib/utils';
import { useAuthContext } from '@/contexts/AuthContext';
import { 
  AlertTriangle, 
  ArrowLeft, 
  Calendar, 
  Clock, 
  Copy, 
  Download, 
  ExternalLink, 
  FileText, 
  Loader2, 
  MoreHorizontal, 
  PenLine, 
  RefreshCw, 
  SendHorizontal,
  Eye,
  FileCheck,
  Users,
  Plus
} from 'lucide-react';
import AddSignatoryDialog from '@/components/business-opportunities/AddSignatoryDialog';

type ContractWithDetails = BusinessContract & {
  proposal: {
    id: string;
    title: string;
    opportunity_id: string;
  };
  customer: {
    id: string;
    name: string;
    business_identification_number?: string;
    legal_name?: string;
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
  };
  signatories: ContractSignatory[];
};

const statusLabels = {
  draft: 'Rascunho',
  sent: 'Enviado',
  signed: 'Assinado',
  active: 'Ativo',
  expired: 'Expirado',
  canceled: 'Cancelado'
};

const statusColors = {
  draft: 'bg-gray-100 text-gray-800',
  sent: 'bg-blue-100 text-blue-800',
  signed: 'bg-purple-100 text-purple-800',
  active: 'bg-green-100 text-green-800',
  expired: 'bg-amber-100 text-amber-800',
  canceled: 'bg-red-100 text-red-800'
};

const templateTypeLabels = {
  consultancy: 'Consultoria',
  licensing: 'Licenciamento',
  services: 'Serviços',
  products: 'Produtos',
  custom: 'Personalizado'
};

const signatoryRoleLabels = {
  contractor: 'Contratante',
  contractee: 'Contratada',
  witness: 'Testemunha'
};

const ContractDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuthContext();

  const [contract, setContract] = useState<ContractWithDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showGeneratePdfDialog, setShowGeneratePdfDialog] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showAddSignatoryDialog, setShowAddSignatoryDialog] = useState(false);

  useEffect(() => {
    const fetchContractData = async () => {
      try {
        setIsLoading(true);
        if (id) {
          const contractData = await getBusinessContract(id);
          setContract(contractData);
        }
      } catch (error) {
        console.error('Error fetching contract data:', error);
        toast({
          title: 'Erro ao carregar contrato',
          description: 'Não foi possível carregar os dados do contrato.',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchContractData();
  }, [id, toast]);

  const handleBackClick = () => {
    navigate(-1);
  };

  const downloadPdf = () => {
    if (contract?.pdf_url) {
      window.open(contract.pdf_url, '_blank');
    } else {
      toast({
        title: 'PDF não disponível',
        description: 'O PDF deste contrato ainda não foi gerado.',
        variant: 'destructive',
      });
    }
  };

  const handleSignatoryAdded = (signatory: ContractSignatory) => {
    setContract(prevContract => {
      if (!prevContract) return null;
      
      return {
        ...prevContract,
        signatories: [
          ...(prevContract.signatories || []),
          signatory
        ]
      };
    });
    
    toast({
      title: 'Signatário adicionado',
      description: 'O signatário foi adicionado com sucesso ao contrato.',
    });
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-6 flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (!contract) {
    return (
      <div className="container mx-auto py-6">
        <div className="text-center">
          <h2 className="text-2xl font-extrabold">Contrato não encontrado</h2>
          <p className="text-muted-foreground mt-2">
            O contrato solicitado não existe ou não foi possível carregá-lo.
          </p>
          <Button onClick={() => navigate(-1)} className="mt-4">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="icon" onClick={handleBackClick}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-extrabold">{contract.title}</h1>
            <p className="text-muted-foreground">
              Contrato para {contract.customer.name}
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <Button
            variant={contract.status === 'draft' ? 'default' : 'outline'}
            onClick={() => {
              // Implement send functionality
              toast({
                title: 'Funcionalidade não implementada',
                description: 'O envio de contratos será implementado posteriormente.',
              });
            }}
            disabled={contract.status !== 'draft'}
          >
            <SendHorizontal className="mr-2 h-4 w-4" />
            Enviar Contrato
          </Button>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => {
                setShowGeneratePdfDialog(true);
              }}>
                <FileText className="mr-2 h-4 w-4" />
                Gerar PDF
              </DropdownMenuItem>
              
              {contract.pdf_url && (
                <DropdownMenuItem onClick={downloadPdf}>
                  <Download className="mr-2 h-4 w-4" />
                  Baixar PDF
                </DropdownMenuItem>
              )}
              
              <DropdownMenuItem onClick={() => navigate(`/proposals/${contract.proposal.id}`)}>
                <Eye className="mr-2 h-4 w-4" />
                Ver Proposta
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-6 mb-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-extrabold text-muted-foreground">Status</CardTitle>
          </CardHeader>
          <CardContent>
            <Badge className={statusColors[contract.status]}>
              {statusLabels[contract.status]}
            </Badge>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-extrabold text-muted-foreground">Valor Total</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-extrabold">
              {formatCurrency(contract.total_value, contract.currency_code)}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-extrabold text-muted-foreground">Data de Início</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-extrabold">
              {contract.start_date
                ? new Date(contract.start_date).toLocaleDateString('pt-BR')
                : 'Não definida'}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-extrabold text-muted-foreground">Data de Término</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-extrabold">
              {contract.end_date
                ? new Date(contract.end_date).toLocaleDateString('pt-BR')
                : 'Não definida'}
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="details" className="space-y-4">
        <TabsList>
          <TabsTrigger value="details">Detalhes</TabsTrigger>
          <TabsTrigger value="signatories">Signatários</TabsTrigger>
        </TabsList>
        
        <TabsContent value="details" className="space-y-4">
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Dados do Contrato</CardTitle>
                <CardDescription>Informações detalhadas sobre o contrato</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="text-sm font-extrabold text-muted-foreground mb-1">Título</h4>
                  <p>{contract.title}</p>
                </div>
                
                {contract.description && (
                  <div>
                    <h4 className="text-sm font-extrabold text-muted-foreground mb-1">Descrição</h4>
                    <p>{contract.description}</p>
                  </div>
                )}
                
                <div>
                  <h4 className="text-sm font-extrabold text-muted-foreground mb-1">Tipo de Contrato</h4>
                  <p>{templateTypeLabels[contract.template_type]}</p>
                </div>
                
                <div>
                  <h4 className="text-sm font-extrabold text-muted-foreground mb-1">Status</h4>
                  <Badge className={statusColors[contract.status]}>
                    {statusLabels[contract.status]}
                  </Badge>
                </div>
                
                <div>
                  <h4 className="text-sm font-extrabold text-muted-foreground mb-1">Proposta Relacionada</h4>
                  <p>
                    <Button 
                      variant="link" 
                      className="p-0 h-auto" 
                      onClick={() => navigate(`/proposals/${contract.proposal.id}`)}
                    >
                      {contract.proposal.title}
                    </Button>
                  </p>
                </div>
                
                <div>
                  <h4 className="text-sm font-extrabold text-muted-foreground mb-1">Valor Total</h4>
                  <p>{formatCurrency(contract.total_value, contract.currency_code)}</p>
                </div>
                
                <div>
                  <h4 className="text-sm font-extrabold text-muted-foreground mb-1">Período</h4>
                  <p>
                    {contract.start_date
                      ? new Date(contract.start_date).toLocaleDateString('pt-BR')
                      : 'Não definida'}
                    {contract.end_date && (
                      <> até {new Date(contract.end_date).toLocaleDateString('pt-BR')}</>
                    )}
                  </p>
                </div>
                
                <div>
                  <h4 className="text-sm font-extrabold text-muted-foreground mb-1">Criado em</h4>
                  <p>{new Date(contract.created_at).toLocaleDateString('pt-BR')}</p>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Dados do Cliente</CardTitle>
                <CardDescription>Informações do cliente relacionado ao contrato</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="text-sm font-extrabold text-muted-foreground mb-1">Nome</h4>
                  <p>{contract.customer.name}</p>
                </div>
                
                {contract.customer.business_identification_number && (
                  <div>
                    <h4 className="text-sm font-extrabold text-muted-foreground mb-1">CNPJ</h4>
                    <p>{contract.customer.business_identification_number}</p>
                  </div>
                )}
                
                {contract.customer.legal_name && (
                  <div>
                    <h4 className="text-sm font-extrabold text-muted-foreground mb-1">Razão Social</h4>
                    <p>{contract.customer.legal_name}</p>
                  </div>
                )}
                
                {contract.customer.address && (
                  <div>
                    <h4 className="text-sm font-extrabold text-muted-foreground mb-1">Endereço</h4>
                    <p>
                      {contract.customer.address}
                      {contract.customer.city && `, ${contract.customer.city}`}
                      {contract.customer.state && `, ${contract.customer.state}`}
                      {contract.customer.zip && ` - ${contract.customer.zip}`}
                    </p>
                  </div>
                )}
                
                <div className="pt-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => navigate(`/customers/${contract.customer.id}`)}
                  >
                    Ver Perfil do Cliente
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        <TabsContent value="signatories">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Signatários do Contrato</CardTitle>
                <CardDescription>Lista de pessoas que devem assinar este contrato</CardDescription>
              </div>
              <Button onClick={() => setShowAddSignatoryDialog(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Adicionar Signatário
              </Button>
            </CardHeader>
            <CardContent>
              {contract.signatories && contract.signatories.length > 0 ? (
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nome</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Cargo</TableHead>
                        <TableHead>Papel</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {contract.signatories.map((signatory) => (
                        <TableRow key={signatory.id}>
                          <TableCell className="font-extrabold">{signatory.name}</TableCell>
                          <TableCell>{signatory.email}</TableCell>
                          <TableCell>{signatory.title || '-'}</TableCell>
                          <TableCell>{signatoryRoleLabels[signatory.role]}</TableCell>
                          <TableCell>
                            {signatory.signed_at ? (
                              <Badge className="bg-green-100 text-green-800">
                                Assinado em {new Date(signatory.signed_at).toLocaleDateString('pt-BR')}
                              </Badge>
                            ) : (
                              <Badge className="bg-amber-100 text-amber-800">
                                Pendente
                              </Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center p-8 text-center">
                  <Users className="h-12 w-12 text-muted-foreground opacity-50 mb-2" />
                  <h3 className="text-lg font-extrabold">Nenhum signatário adicionado</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Adicione signatários ao contrato clicando no botão acima.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={showGeneratePdfDialog} onOpenChange={setShowGeneratePdfDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Gerar PDF do Contrato</DialogTitle>
            <DialogDescription>
              A funcionalidade de geração de PDF para contratos será implementada posteriormente.
            </DialogDescription>
          </DialogHeader>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGeneratePdfDialog(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {contract.pdf_url && (
        <iframe
          src={contract.pdf_url}
          className="w-full h-[600px] border border-gray-200 rounded-md mt-6"
          title="Visualização do Contrato"
        ></iframe>
      )}

      {contract && (
        <AddSignatoryDialog
          open={showAddSignatoryDialog}
          onOpenChange={setShowAddSignatoryDialog}
          contract={contract}
          onSignatoryAdded={handleSignatoryAdded}
        />
      )}
    </div>
  );
};

export default ContractDetail; 